<template>
  <property-report-card
    title="Future Availability"
    description="See upcoming unit availabilities based recent on notices to vacate."
    :printing-mode="printingMode"
    :is-empty="!unitsFound(filteredProperties)"
  >
    <template #actions>
      <div class="trailing-text">Future 6 months</div>
    </template>
    <q-separator />
    <q-card-section>
      <div id="intercom-future-availability-content" style="overflow-x: auto">
        <div style="width: 100%; height: 300px">
          <bar class="chart" :data="chartData" :options="chartOptions" :plugins="chartPlugins" />
        </div>
      </div>
    </q-card-section>
    <q-separator v-if="!printingMode" />
    <q-card-section v-if="!printingMode">
      <q-card flat>
        <div v-if="userSelection && selectedRange" class="flex justify-between q-mb-md">
          <div class="text-bold q-mb-md q-my-auto">
            Units Available Between {{ selectedRange.from }} and {{ selectedRange.to }}
            <q-icon name="close" class="q-ml-md cursor-pointer" @click="closePanel" />
          </div>
          <q-btn flat no-caps class="q-my-auto" @click="exportCSV">
            <q-icon name="download" class="q-mr-sm" size="medium" />Download
          </q-btn>
        </div>
        <div
          v-if="!tableUnits.length"
          class="full-width text-center justify-center flex align-center"
        >
          <q-icon name="info" size="18px" color="accent" class="q-mr-sm q-my-auto" />
          Click on a bar in the chart above to see the units that make it up.
        </div>
        <unit-details-table
          v-else
          :rows="tableUnits"
          :expanded-unit-history="expandedUnitHistory"
          :date-range="dateRange"
        />
      </q-card>
    </q-card-section>
  </property-report-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { addDays, eachWeekOfInterval, format, parseISO, min, max, addMonths } from 'date-fns'
import { enUS } from 'date-fns/locale/en-US'
import { Bar } from 'vue-chartjs'
import type { ChartOptions } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { unitsFound, unitTypeColor } from '@/utils'

import type { PropertyDetailsResponse, Availability, AvailabilityPeriod, Price } from 'common'

import UnitDetailsTable, { exportCSV } from './UnitDetailsTable.vue'
import PropertyReportCard from '@/components/PropertyReportCard.vue'

export default {
  name: 'PropertyCompareFutureAvailability',
  components: { PropertyReportCard, Bar, UnitDetailsTable },
  props: {
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    },
    expandedUnitHistory: {
      type: Object as PropType<{
        [unit_id: string]: {
          created_on: string
          history: Price
        }[]
      }>,
      required: true
    },
    dateRange: {
      type: Object as PropType<{ from: Date; to: Date }>,
      required: true
    },
    printingMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      userSelection: null as {
        week: string
        beds: number | null
      } | null,
      chartPlugins: [ChartDataLabels]
    }
  },
  computed: {
    selectedRange() {
      if (this.userSelection === null) {
        return null
      }

      const date = parseISO(this.userSelection.week)
      const formattedFrom = format(date, 'M/d/yy')
      const formattedTo = format(addDays(date, 6), 'M/d/yy')

      this.$tracker.clickedOnGraph('future_availability')

      return {
        from: formattedFrom,
        to: formattedTo
      }
    },
    generateData() {
      const sixMonthsOut = addDays(new Date(), 180).toISOString()
      let availability = this.filteredProperties.flatMap(({ prop, units }) => {
        return units
          .filter(({ period }) => {
            return (
              !period.exit_market &&
              period.availability &&
              period.availability > prop.created_on &&
              period.availability > new Date().toISOString() &&
              period.availability < sixMonthsOut
            )
          })
          .map(({ period }) => parseISO(period.availability))
      })
      if (!availability.length) {
        availability = [new Date(), addMonths(new Date(), 6)]
      }
      return eachWeekOfInterval(
        {
          start: min(availability),
          end: max(availability)
        },
        {
          weekStartsOn: 1,
          locale: enUS
        }
      ).map((s) => {
        const start = format(s, 'yyyy-MM-dd')
        const end = format(addDays(start, 6), 'yyyy-MM-dd')
        const weekUnits = this.filteredProperties.flatMap(({ prop, units }) => {
          return units
            .filter(
              ({ period }) =>
                period.availability &&
                period.availability >= start &&
                period.availability > prop.created_on &&
                period.availability <= end &&
                !period.exit_market
            )
            .map(({ unit, lastHistory, period }) => ({
              unit,
              lastHistory,
              period,
              prop
            }))
        })
        return {
          week: start.split('T')[0],
          units: weekUnits
        }
      })
    },
    relevantBeds() {
      const beds = [
        ...new Set(
          this.filteredProperties.flatMap(({ units }) => units.map(({ unit }) => unit.bed))
        )
      ]
      beds.sort()
      return beds as number[]
    },
    chartData() {
      const selectedWeek = this.userSelection?.week ?? ''
      const selectedBeds = this.userSelection?.beds ?? -1
      const datasets: {
        label: string
        borderColor: string
        backgroundColor: (ctx: { dataIndex: number; datasetIndex: number }) => string
        data: number[]
        borderWidth: number
        datalabels: {
          align: string
          anchor: string
          font: { weight: string }
          formatter: (value: string) => string
        }
      }[] = []
      this.relevantBeds.forEach((bed) => {
        const dataset = {
          label: `${bed}BR`,
          borderColor: unitTypeColor(bed),
          backgroundColor: (ctx: { dataIndex: number; datasetIndex: number }) => {
            if (
              selectedWeek === this.generateData[ctx.dataIndex].week &&
              ctx.datasetIndex === this.relevantBeds.indexOf(selectedBeds)
            ) {
              return unitTypeColor(bed)
            }
            return unitTypeColor(bed, 0.6)
          },
          data: this.generateData.map((col) => col.units.filter((_) => _.unit.bed === bed).length),
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.9,
          maxBarThickness: 150,
          datalabels: {
            align: 'center',
            anchor: 'center',
            font: { weight: 'bold' },
            color: this.$q.dark.isActive ? 'white' : undefined,
            formatter: (value: string) => value || ''
          }
        }
        datasets.push(dataset)
      })
      if (datasets.length) {
        datasets[datasets.length - 1].datalabels.align = 'top'
        datasets[datasets.length - 1].datalabels.anchor = 'end'
      }
      return {
        labels: this.generateData.map(
          (data) => this.formatWeek(data.week) // format into 3/21 - 3/28
        ),
        datasets
      }
    },
    tableUnits() {
      if (this.userSelection?.week) {
        return this.generateData
          .find((_) => _.week === this.userSelection?.week)!
          .units.filter(({ unit }) => {
            let pass = true
            if (this.userSelection?.beds != null) {
              pass = pass && this.userSelection.beds === unit.bed
            }
            return pass
          })
      }
      return []
    },
    chartOptions() {
      return {
        onClick: (_: unknown, clickedElements) => {
          if (this.printingMode) {
            return
          }
          if (clickedElements.length === 0) {
            return
          }
          const { dataIndex, datasetIndex } = clickedElements[0].element.$context
          this.userSelection = {
            week: this.generateData[dataIndex].week,
            beds: this.relevantBeds[datasetIndex]
          }
        },
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: {
            onClick: () => null,
            display: true,
            position: 'top',
            labels: {
              boxWidth: 10,
              boxHeight: 10,
              font: {
                size: 12
              }
            }
          }
        },
        options: {
          scales: {
            x: {
              type: 'time',
              time: {
                unit: 'week'
              },
              min: this.dateRange.from.valueOf(),
              max: this.dateRange.to.valueOf()
            }
          }
        },
        scales: {
          x: {
            stacked: true
          },
          y: {
            stacked: true,
            grace: '25%'
          }
        }
      } as ChartOptions<'bar'>
    }
  },
  watch: {
    dateRange() {
      this.userSelection = null
    }
  },
  methods: {
    unitsFound,
    exportCSV() {
      exportCSV(this.tableUnits, 'future-availability', this.dateRange)
      this.$tracker.exported('future_availability')
    },
    closePanel() {
      this.userSelection = null
    },
    formatWeek(date: string, days = 6) {
      const fromDate = parseISO(date)
      const toDate = addDays(fromDate, days)
      return `${format(fromDate, 'M/d')} - ${format(toDate, 'M/d')}`
    }
  }
}
</script>
<style scoped>
.trailing-text {
  font-size: 1em;
  color: #999;
  font-weight: 500;
}
.chart {
  height: 450px;
  width: 100%;
}
</style>
